import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Logo from "../../../assets/icons/svg/Cluva/Logo-with-text.svg";
import { ReactComponent as SigninHeaderIcon } from "../../../assets/icons/svg/Cluva/Signin_header_image.svg";
import { API_URLS } from "../../../api/urls";
import useDocumentTitle from "../../../hooks/useDocumentTitle";
import Lottie from "lottie-react";
import DotLoader from "../../../assets/Animation/Dot Loader.json";
import { DEFAULT_FONT_FAMILY } from "../../../config/fontConfig";
import Button from "../../ui/buttons";
import OTPComponent from "./OtpScreen";
import {
  initGoogleAuth,
  renderGoogleButton,
  setGoogleAuthCallback,
  GoogleAuthResult,
} from "../../../config/googleAuth";
import { GoogleAuthService } from "../../../services/googleAuthService";
import { X, AlertCircle } from "lucide-react";

const extractErrorMessage = (error: any): string => {
  if (typeof error === "string") return error;
  if (error && typeof error === "object") {
    if (error.message) return typeof error.message === "string" ? error.message : "An error occurred";
    if (error.detail) return typeof error.detail === "string" ? error.detail : "An error occurred";
    if (error.msg) return typeof error.msg === "string" ? error.msg : "An error occurred";
    if (Array.isArray(error) && error[0]?.msg) return error[0].msg;
  }
  return "An unexpected error occurred";
};

const SignInCard: React.FC = () => {
  useDocumentTitle("Log In");

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [accountNotFound, setAccountNotFound] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const gisContainerRef = useRef<HTMLDivElement>(null);
  const isInGooglePopup = useRef(false);
  const resultHandlerRef = useRef<((result: GoogleAuthResult) => void) | null>(null);

  const fontFamily = DEFAULT_FONT_FAMILY;

  const locationState = location.state as {
    returnUrl?: string;
    returnState?: any;
    message?: string;
    developerEmail?: string;
  } | null;

  // Always keep the result handler fresh (captures latest navigate / locationState)
  resultHandlerRef.current = async (result: GoogleAuthResult) => {
    isInGooglePopup.current = false;

    if (!result.success || !result.googleIdToken) {
      if (result.error !== "Sign-in cancelled") {
        showErrorToastMessage(result.error || "Google sign-in failed");
      }
      setGoogleLoading(false);
      return;
    }

    setGoogleLoading(true);

    const backendResult = await GoogleAuthService.authenticateWithBackend(
      result.googleIdToken,
      result.googleUserInfo,
    );

    if (!backendResult.success) {
      showErrorToastMessage(backendResult.error || "Authentication failed");
      setGoogleLoading(false);
      return;
    }

    await GoogleAuthService.navigateAfterAuth(
      backendResult.needsOnboarding || false,
      backendResult.userProfile,
      navigate,
      locationState,
    );
  };

  useEffect(() => {
    const setup = async () => {
      try {
        await initGoogleAuth();
        if (gisContainerRef.current) {
          renderGoogleButton(gisContainerRef.current, (result) => {
            resultHandlerRef.current?.(result);
          });
        }
      } catch (err) {
        console.error("Failed to initialise Google auth:", err);
      }
    };

    setup();

    // Reset loading if popup is closed without completing sign-in
    const onWindowFocus = () => {
      if (isInGooglePopup.current) {
        setTimeout(() => {
          if (isInGooglePopup.current) {
            isInGooglePopup.current = false;
            setGoogleLoading(false);
          }
        }, 300);
      }
    };

    window.addEventListener("focus", onWindowFocus);
    return () => {
      window.removeEventListener("focus", onWindowFocus);
      setGoogleAuthCallback(null);
    };
  }, []);

  // Restore deep-link state if present
  useEffect(() => {
    if (!locationState?.returnUrl) {
      const pendingDeepLink = sessionStorage.getItem("pending_deep_link");
      if (pendingDeepLink) {
        try {
          const deepLinkInfo = JSON.parse(pendingDeepLink);
          navigate(location.pathname, {
            state: {
              returnUrl: `/view-card/${deepLinkInfo.cardId}?fromDeepLink=true`,
              returnState: {
                cardId: deepLinkInfo.cardId,
                fromDeepLink: true,
                returnToDashboard: true,
                developerEmail: deepLinkInfo.developerEmail,
                activeTab: "all",
              },
              message: "Please sign in to view this feedback",
              developerEmail: deepLinkInfo.developerEmail,
            },
            replace: true,
          });
        } catch (error) {
          console.error("Failed to restore deep link info:", error);
        }
      }
    }
  }, [navigate, location.pathname, locationState?.returnUrl]);

  // Auto-dismiss error toast after 5 s
  useEffect(() => {
    if (showErrorToast) {
      const timer = setTimeout(() => setShowErrorToast(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showErrorToast]);

  const showErrorToastMessage = (message: string | any) => {
    setErrorMessage(extractErrorMessage(message));
    setShowErrorToast(true);
  };

  const isValidEmail = (email: string): boolean => {
    if (/^\d+$/.test(email)) return false;
    return /^[a-zA-Z0-9._]+@[a-zA-Z]+\.[a-zA-Z]{2,}$/.test(email);
  };

  const handleSignIn = async () => {
    if (!email.trim() || !isValidEmail(email)) {
      setShowError(true);
      return;
    }
    setAccountNotFound(false);
    setLoading(true);
    try {
      const response = await fetch(API_URLS.LOGIN_REQUEST, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (response.ok) {
        setShowOtpScreen(true);
      } else {
        let errorData;
        try { errorData = await response.json(); }
        catch { errorData = { message: "Sign in failed. Please try again." }; }

        if (response.status === 404) {
          setAccountNotFound(true);
        } else {
          const msg = extractErrorMessage(errorData).toLowerCase();
          if (
            msg.includes("not found") ||
            msg.includes("no account") ||
            msg.includes("not registered") ||
            msg.includes("does not exist")
          ) {
            setAccountNotFound(true);
          } else {
            showErrorToastMessage(errorData);
          }
        }
      }
    } catch {
      showErrorToastMessage("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value.toLowerCase());
    if (showError) setShowError(false);
    if (accountNotFound) setAccountNotFound(false);
  };

  const getErrorMessage = () => {
    if (!email.trim()) return "Email is required";
    if (!isValidEmail(email)) return "Please enter a valid email address";
    return "";
  };

  return (
    <div className="min-h-screen flex bg-white" style={{ fontFamily }}>

      {/* ── Error Toast ────────────────────────────────────────────── */}
      {showErrorToast && (
        <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-2 duration-300">
          <div
            className="bg-red-50 border border-red-200 rounded-lg shadow-lg p-4 flex items-start gap-2 max-w-sm"
            style={{ fontFamily }}
          >
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800" style={{ fontFamily }}>
                {errorMessage}
              </p>
            </div>
            <button
              onClick={() => setShowErrorToast(false)}
              className="text-red-400 hover:text-red-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* ── Left Side — Form ────────────────────────────────────────── */}
      <div className="w-1/2 flex flex-col px-16" style={{ fontFamily }}>
        <div className="w-full pt-24 2xl:pt-28 3xl:pt-40 4xl:pt-48 flex justify-center">
          <div className="max-w-md 3xl:max-w-lg 4xl-max-w-xl w-full">

            <div className="flex justify-start mb-10">
              <img src={Logo} alt="Logo" className="h-8 w-auto" />
            </div>

            {locationState?.message && !showOtpScreen && (
              <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg text-center" style={{ fontFamily }}>
                <p className="text-sm text-blue-800" style={{ fontFamily }}>{locationState.message}</p>
              </div>
            )}

            {!showOtpScreen ? (
              <>
                <h1 className="text-3xl font-bold text-[#1F2937]" style={{ fontFamily }}>
                  Welcome back
                </h1>
                <p className="text-[#6B7280] mb-6 text-[16px] 3xl:text-[18px]" style={{ fontFamily }}>
                  Please enter your registered email ID to log in
                </p>

                <div>
                  {/* Email field */}
                  <div className="space-y-2 mb-4">
                    <input
                      type="email"
                      placeholder="yourname@email.com"
                      value={email}
                      onChange={handleEmailChange}
                      onKeyDown={(e) => { if (e.key === "Enter" && !loading) handleSignIn(); }}
                      className={`w-full px-4 py-3 text-[16px] border-[0.5px] rounded-lg font-inter focus:outline-none focus:ring-1 focus:ring-[#7ABFFF] focus:border-transparent ${showError ? "border-red-500" : "border-[#ADAFB7]"}`}
                      disabled={loading || googleLoading}
                    />
                    {showError && (
                      <p className="text-red-500 text-sm mt-1" style={{ fontFamily }}>{getErrorMessage()}</p>
                    )}
                    {accountNotFound && (
                      <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-800" style={{ fontFamily }}>
                          No account found for <span className="font-medium">{email}</span>.{" "}
                          <button
                            onClick={() => navigate("/signup", {
                              state: {
                                returnUrl: locationState?.returnUrl,
                                returnState: locationState?.returnState,
                                message: locationState?.message,
                                developerEmail: locationState?.developerEmail,
                              },
                            })}
                            className="text-[#4D98DD] font-medium hover:underline"
                            style={{ fontFamily }}
                          >
                            Create an account →
                          </button>
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-6">
                    {/* Email log-in button */}
                    <Button
                      variant="primary"
                      className="w-full font-semibold py-3 cursor-pointer"
                      onClick={handleSignIn}
                      style={{ fontFamily }}
                      disabled={loading || googleLoading}
                    >
                      {loading ? (
                        <span className="inline-block relative" style={{ width: 40, height: 16 }}>
                          <Lottie animationData={DotLoader} loop style={{ width: 120, height: 48, position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }} />
                        </span>
                      ) : "Log in"}
                    </Button>

                    <div className="flex items-center justify-center gap-4 my-6">
  <hr className="w-16 border-gray-300" />
  <span className="text-sm text-gray-500" style={{ fontFamily }}>OR</span>
  <hr className="w-16 border-gray-300" />
</div>

                    {/* Google Sign-In button
                        The custom button is the visible design.
                        The GIS container sits on top as a transparent overlay so GIS
                        receives the real click and opens its popup in a trusted context. */}
                    <div
                      className="relative w-full group"
                      style={{ cursor: loading || googleLoading ? "not-allowed" : "pointer" }}
                      onMouseDown={() => {
                        if (!loading && !googleLoading) isInGooglePopup.current = true;
                      }}
                    >
                      {/* Original custom button — visual design only */}
                      <div
                        className={`w-full flex justify-center gap-3 border border-gray-300 py-3 rounded-lg transition-colors ${loading || googleLoading ? "opacity-50" : "group-hover:bg-gray-50"}`}
                        style={{ pointerEvents: "none", userSelect: "none" }}
                      >
                        <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" className="w-5 h-5" />
                        <span className="text-[#030507] font-medium text-[16px]" style={{ fontFamily }}>
                          Login with Google
                        </span>
                      </div>

                      {/* GIS button — transparent overlay that receives the click */}
                      <div
                        ref={gisContainerRef}
                        style={{
                          position: "absolute",
                          inset: 0,
                          opacity: 0,
                          overflow: "hidden",
                          pointerEvents: loading || googleLoading ? "none" : "auto",
                        }}
                      />

                      {/* Loading overlay during backend auth */}
                      {googleLoading && (
                        <div className="absolute inset-0 flex items-center justify-center border border-gray-300 rounded-lg bg-white bg-opacity-90">
                          <span className="inline-block relative" style={{ width: 40, height: 16 }}>
                            <Lottie
                              animationData={DotLoader}
                              loop
                              style={{
                                width: 120, height: 48,
                                position: "absolute", top: "50%", left: "50%",
                                transform: "translate(-50%, -50%)",
                                filter: "grayscale(1) brightness(0)",
                              }}
                            />
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="text-center mt-8">
                    <p className="text-[#030507] text-[16px]" style={{ fontFamily }}>
                      Don't have an account?{" "}
                      <button
                        onClick={() => navigate("/signup", {
                          state: {
                            returnUrl: locationState?.returnUrl,
                            returnState: locationState?.returnState,
                            message: locationState?.message,
                            developerEmail: locationState?.developerEmail,
                          },
                        })}
                        className="text-[#4D98DD] font-medium text-[16px] hover:underline"
                        style={{ fontFamily }}
                        disabled={loading || googleLoading}
                      >
                        Create account
                      </button>
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <OTPComponent
                email={email}
                isSignUp={false}
                returnUrl={locationState?.returnUrl}
                returnState={locationState?.returnState}
                onBack={() => setShowOtpScreen(false)}
              />
            )}
          </div>
        </div>

        {!showOtpScreen && (
          <div className="w-full mt-auto pb-8 flex justify-center">
            <div className="max-w-md w-full">
              <p className="text-sm text-gray-500 text-center" style={{ fontFamily }}>
                By clicking "Log in" or "Login with Google" you agree to our{" "}
                <a href="https://cluva.io/terms-of-use" target="_blank" rel="noopener noreferrer" className="text-[#4D98DD] hover:underline" style={{ fontFamily }}>Terms of Use</a>{" "}
                and acknowledge that you have read and understand our{" "}
                <a href="https://cluva.io/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#4D98DD] hover:underline" style={{ fontFamily }}>Privacy Policy</a>.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ── Right Side — Illustration ───────────────────────────────── */}
      <div className="w-full lg:w-1/2 relative overflow-hidden min-h-48 sm:min-h-64 lg:min-h-screen order-1 lg:order-2">
        <div className="absolute inset-0 w-full h-full">
          <SigninHeaderIcon className="w-full h-full object-cover object-right scale-110 translate-x-8" />
        </div>
      </div>
    </div>
  );
};

export default SignInCard;
